/* Bureaucromancy UI kit — walnut, brass, parchment, wax (docs/04 §10). */
:root {
  --walnut-900: #140e0b;
  --walnut-800: #1d1511;
  --walnut-700: #2a1e17;
  --walnut-600: #3a2a20;
  --walnut-500: #54392a;
  --brass: #c49a52;
  --brass-hi: #e8c774;
  --parchment: #efe4cb;
  --parchment-2: #e3d4b3;
  --ink: #2b2018;
  --ink-soft: #5d4a36;
  --muted: #b9a78a;
  --wax: #7a1f2b;
  --red: #c8443a;
  --yellow: #e0b43c;
  --blue: #3f7fe0;
  --green: #3fa86a;
  --mana: #f26bbd; /* pink: clearly apart from the Blue permit */
  --gold: #d8a93a;
  --good: #7fcf8f;
  --warn: #ff8a6a;
  --font-display: 'Cormorant Garamond', Georgia, serif;
  --font-body: 'Alegreya Sans', system-ui, sans-serif;
  --font-stamp: 'Special Elite', 'Courier New', monospace;
  --radius: 10px;
  color-scheme: dark;
}

* { box-sizing: border-box; }
html, body, #root { height: 100%; margin: 0; }
/* Touch: no blue tap flash (a document-level click listener makes the whole page count as one
   tappable element), no double-tap zoom or long-press callouts over the game. */
html {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  touch-action: manipulation;
}
body {
  background: radial-gradient(ellipse at 50% 0%, #3a261a 0%, var(--walnut-900) 70%);
  color: var(--parchment);
  font-family: var(--font-body);
  font-size: 15px;
  -webkit-font-smoothing: antialiased;
}
button, input, select { font: inherit; color: inherit; }
button {
  cursor: pointer;
  background: linear-gradient(#4a3426, #33241b);
  border: 1px solid var(--walnut-500);
  border-bottom-color: #1c130e;
  border-radius: 8px;
  padding: 6px 12px;
  color: var(--parchment);
  transition: transform 0.08s ease, border-color 0.15s ease, background 0.15s ease;
}
button:hover:not(:disabled) { border-color: var(--brass); }
button:active:not(:disabled) { transform: translateY(1px); }
button:disabled { opacity: 0.45; cursor: not-allowed; }
button:focus-visible, [tabindex]:focus-visible { outline: 2px solid var(--brass-hi); outline-offset: 2px; }
button.is-on { border-color: var(--brass-hi); background: linear-gradient(#6a4b32, #4a3426); }
button.danger { border-color: #8e2a2a; }
button.link { background: none; border: none; color: var(--muted); text-decoration: underline; }
button.small { padding: 3px 8px; font-size: 13px; }
input, select {
  background: #1a120e;
  border: 1px solid var(--walnut-500);
  border-radius: 8px;
  padding: 7px 10px;
}
h1, h2, h3, h4 { font-family: var(--font-display); font-weight: 700; margin: 0 0 8px; letter-spacing: 0.01em; }
.hint { color: var(--muted); font-size: 13px; margin: 4px 0; }
.error { color: var(--warn); }
.row { display: flex; gap: 8px; align-items: center; margin-top: 8px; }
.row.wrap, .wrap { flex-wrap: wrap; }

.panel {
  background: linear-gradient(180deg, rgba(42, 30, 23, 0.96), rgba(29, 21, 17, 0.96));
  border: 1px solid var(--walnut-500);
  border-top: 2px solid var(--brass);
  border-radius: var(--radius);
  padding: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}
.panel h3 { font-size: 19px; display: flex; align-items: baseline; gap: 8px; }
.panel h3 .sub { font-family: var(--font-body); font-size: 12px; color: var(--muted); font-weight: 400; }

/* ─── Glyphs: shape + colour (colour-blind safe, docs/04 §6) ─── */
.glyph { display: inline-block; width: 1em; height: 1em; vertical-align: -0.12em; margin: 0 1px; }
.glyph-red { background: var(--red); clip-path: polygon(50% 4%, 100% 94%, 0 94%); }
.glyph-yellow { background: var(--yellow); clip-path: polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0 50%); }
.glyph-blue { background: var(--blue); clip-path: polygon(50% 0, 100% 50%, 50% 100%, 0 50%); }
.glyph-green { background: var(--green); border-radius: 50%; box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.25); }
.glyph-mana { background: var(--mana); border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%; clip-path: polygon(50% 0, 88% 55%, 80% 85%, 50% 100%, 20% 85%, 12% 55%); box-shadow: 0 0 6px var(--mana); }
.glyph-vp { background: radial-gradient(circle at 35% 30%, #fff0bf, #d8b05c 55%, #8a6a2c); border-radius: 50%; }

/* ─── Home ─── */
.home { max-width: 1100px; margin: 0 auto; padding: 32px 16px 48px; }
.home-hero { text-align: center; margin: 24px 0 28px; }
.home-seal { font-size: 42px; width: 78px; height: 78px; line-height: 78px; margin: 0 auto 10px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, #a23446, var(--wax)); box-shadow: 0 0 0 4px #4b1219, 0 8px 24px rgba(0, 0, 0, 0.5); }
.home h1 { font-size: clamp(40px, 7vw, 72px); color: var(--brass-hi); text-shadow: 0 3px 0 #3b2412; }
.tagline { font-size: 18px; color: var(--parchment-2); max-width: 640px; margin: 0 auto; }
.home-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 16px; }
.home-card { display: flex; flex-direction: column; gap: 10px; }
.home-card label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: var(--muted); }
.home-card.how ol { margin: 0; padding-left: 18px; color: var(--parchment-2); line-height: 1.5; }
.seg { display: flex; gap: 4px; flex-wrap: wrap; }
.join-row { display: flex; gap: 6px; }
.join-row input { flex: 1; letter-spacing: 0.3em; text-transform: uppercase; }

/* ─── Stamp buttons (the signature interaction, docs/06 §2) ─── */
.stamp-btn {
  font-family: var(--font-stamp);
  letter-spacing: 0.08em;
  font-size: 15px;
  padding: 10px 18px;
  background: radial-gradient(circle at 30% 25%, #a8323f, var(--wax) 70%);
  border: 2px solid #4b1219;
  border-radius: 10px;
  color: #fbe9d0;
  box-shadow: 0 4px 0 #3a0e14, 0 8px 18px rgba(0, 0, 0, 0.4);
}
.stamp-btn span { display: inline-block; border: 1.5px dashed rgba(251, 233, 208, 0.55); padding: 2px 10px; border-radius: 4px; }
.stamp-btn:active:not(:disabled) { transform: translateY(4px); box-shadow: 0 0 0 #3a0e14, 0 3px 8px rgba(0, 0, 0, 0.4); }
.stamp-btn.small { font-size: 13px; padding: 6px 12px; }

/* ─── Lobby ─── */
.lobby { max-width: 640px; margin: 48px auto; padding: 0 16px; }
.lobby-card { display: flex; flex-direction: column; gap: 10px; }
.code { font-family: var(--font-stamp); letter-spacing: 0.25em; color: var(--brass-hi); }
.invite { display: flex; gap: 6px; }
.invite input { flex: 1; min-width: 0; }
.seat-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.seat-list li { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 8px; background: #1a120e; border-left: 5px solid var(--seal, #555); }
.seat-list li.empty { color: var(--muted); border-left-color: transparent; font-style: italic; }
.seat-list .name { flex: 1; }
.host-controls { display: flex; flex-direction: column; gap: 10px; align-items: flex-start; }
.host-controls label { display: flex; gap: 6px; align-items: center; font-size: 13px; color: var(--muted); }
.flag { font-size: 11px; padding: 1px 7px; border-radius: 999px; background: #33261d; color: var(--muted); }
.flag.warn { background: #4b1f16; color: var(--warn); }

/* Hover-inspect for cards on the table */
@keyframes pulse { from { opacity: 1; } to { opacity: 0.45; } }

/* Cards: every face is the shared canvas render (scene/cardFaces.ts); CSS only sizes and lights it. */
.card-row { display: flex; gap: 8px; flex-wrap: nowrap; overflow-x: auto; padding: 4px; }
.card-row.wrap { flex-wrap: wrap; }
.card {
  position: relative;
  flex: none;
  width: 132px;
  height: 185px;
  border-radius: 7px;
  background: #16100c;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.5), 0 8px 18px rgba(0, 0, 0, 0.45);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  user-select: none;
}
.card > canvas.face { display: block; width: 100%; height: 100%; border-radius: inherit; }
.card.landscape { width: 185px; height: 132px; }
.card.card-sm { width: 112px; height: 157px; border-radius: 6px; }
.card.card-sm.landscape { width: 157px; height: 112px; }
.card.card-lg { width: 240px; height: 336px; border-radius: 12px; }
.card.card-lg.landscape { width: 336px; height: 240px; }
.card.is-clickable { cursor: pointer; }
.card.is-clickable:hover { transform: translateY(-6px); }
.card.is-playable { box-shadow: 0 0 0 2px #f2d58c, 0 0 18px rgba(242, 213, 140, 0.55), 0 8px 18px rgba(0, 0, 0, 0.45); }
.card.is-selected { transform: translateY(-12px); box-shadow: 0 0 0 3px #fff4c4, 0 0 26px rgba(255, 244, 196, 0.8), 0 10px 22px rgba(0, 0, 0, 0.5); }
.card.is-dim { filter: brightness(0.72) saturate(0.85); }
.card.is-used { filter: grayscale(0.6) brightness(0.6); }
.card.empty { background: rgba(255, 255, 255, 0.04); border: 1px dashed var(--walnut-500); box-shadow: none; }
.price-badge { position: absolute; bottom: 6px; right: 6px; z-index: 2; background: var(--wax); color: #fbe9d0; font-weight: 700; font-size: 12px; padding: 2px 8px; border-radius: 999px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45); }
.card-preview { position: fixed; z-index: 60; pointer-events: none; animation: preview-in 0.14s ease-out; }
.card-preview-face { border-radius: 16px; box-shadow: 0 0 0 1px rgba(242, 213, 140, 0.5), 0 24px 60px rgba(0, 0, 0, 0.7); }
.card-preview-face > canvas.face { display: block; width: 100%; height: 100%; border-radius: 16px; }
.card-preview-flavor { margin-top: 8px; padding: 6px 12px; font-family: var(--font-display); font-style: italic; font-size: 16px; line-height: 1.25; color: #e9dcbc; text-align: center; background: rgba(9, 12, 18, 0.88); border: 1px solid rgba(201, 164, 90, 0.45); border-radius: 6px; }
@keyframes preview-in { from { opacity: 0; transform: translateY(8px) scale(0.97); } }

/* Decisions & modals */

/* Toasts */
.toasts { position: fixed; top: 64px; left: 50%; transform: translateX(-50%); z-index: 50; display: flex; flex-direction: column; gap: 6px; align-items: center; pointer-events: none; }
.toast { background: var(--parchment); color: var(--ink); padding: 8px 14px; border-radius: 10px; font-weight: 700; box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4); }
.toast-error { background: #f6d6c6; color: #7a1f14; }
.toast-good { background: #d8f0d6; color: #1f5a2a; }

/* ─── FX layer ─── */
.fx-layer { position: fixed; inset: 0; pointer-events: none; z-index: 35; }
.fx-token { position: fixed; left: 0; top: 0; will-change: transform; }
.fx-token .glyph { font-size: 22px; filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.6)) drop-shadow(0 3px 3px rgba(0, 0, 0, 0.5)); }
.token-mana .glyph { font-size: 20px; }
.token-vp .glyph { font-size: 22px; }
.mini-card { display: block; width: 34px; height: 46px; border-radius: 4px; border: 1px solid #3a0e14; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5); }
.mini-card.back { background: repeating-linear-gradient(45deg, #5a1c26 0 5px, #6b2230 5px 10px); }
.mini-card.spell { background: var(--parchment); border-top: 5px solid var(--accent, var(--brass)); }
.mini-card.s-conjuration { --accent: var(--red); }
.mini-card.s-transmutation { --accent: var(--yellow); }
.mini-card.s-evocation { --accent: var(--blue); }
.mini-card.s-divination { --accent: var(--green); }
.fx-float { position: fixed; left: 0; top: 0; font-family: var(--font-display); font-size: 22px; font-weight: 700; color: #fff4d6; text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8); white-space: nowrap; }
.float-mana { color: #ffd0ec; }
.float-vp { color: #ffe08a; }
.float-spend { color: #ffb4a0; }
.float-ability { font-size: 15px; color: #d7c3ff; }
.fx-banner { position: fixed; left: 50%; top: 22%; transform: translateX(-50%); padding: 12px 40px; text-align: center; background: linear-gradient(90deg, transparent, rgba(29, 21, 17, 0.92) 15%, rgba(29, 21, 17, 0.92) 85%, transparent); border-top: 1px solid var(--brass); border-bottom: 1px solid var(--brass); min-width: 420px; }
.fx-banner-title { font-family: var(--font-display); font-size: 40px; font-weight: 700; color: var(--brass-hi); line-height: 1; }
.fx-banner-sub { font-family: var(--font-stamp); font-size: 14px; color: var(--parchment-2); margin-top: 6px; }
.banner-turn .fx-banner-title { color: #fff1c9; }
.banner-rank .fx-banner-title { color: var(--gold); }
.showcase { position: fixed; inset: 0; display: grid; place-items: center; }
.showcase-flare { position: absolute; width: 520px; height: 520px; border-radius: 50%; }
.showcase-card { position: relative; filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.6)); }
.showcase-stamp { position: absolute; left: 50%; top: 52%; translate: -50% -50%; font-family: var(--font-stamp); font-size: 34px; letter-spacing: 0.06em; padding: 4px 14px; border: 4px double; border-radius: 8px; background: rgba(246, 236, 212, 0.35); text-shadow: 0 0 1px currentColor; mix-blend-mode: multiply; white-space: nowrap; }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
}
/* Cards and seals in flight between players and the table: large enough to follow by eye. */
.token-card .mini-card { width: 46px; height: 64px; border-radius: 5px; box-shadow: 0 8px 18px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(242, 213, 140, 0.45); }
.mini-card.back.spell { background: repeating-linear-gradient(45deg, #1f3a6a 0 5px, #28477d 5px 10px); border-color: #0b1630; }
.mini-card.back.action { background: repeating-linear-gradient(45deg, #8a2e42 0 5px, #9a3a4e 5px 10px); border-color: #2a0a12; }
.seal-token { display: block; width: 30px; height: 30px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, color-mix(in srgb, var(--seal) 60%, white), var(--seal) 60%, color-mix(in srgb, var(--seal) 60%, black)); box-shadow: 0 0 0 2px #0b0f16, 0 0 0 3px var(--gilt), 0 6px 14px rgba(0, 0, 0, 0.5); }
